import { useState, useRef } from 'react';
import { pm2Header } from '@pm2/design-tokens';
import { pm2InterfaceStore } from '@pm2/store';
import { isPlatformNodeJS, stopScrolling, scrollTo } from '@utils/common';
import { useForceHeaderCollapsed, useScrollLocking } from '@utils/react';
import { HEIGHT_TRANSITION_DURATION_MS } from './comp.harmonica-iframe.style.js';

const USE_SCROLL_LOCKING_FEATURE = isPlatformNodeJS() ? false : new URL(location.href).searchParams.get("harmonica-iframe-scroll-locking") === "true";

/**
 * Returns state and functions required to expand and collapse the component.
 */
function useExpandableIframe(rootRef, disabled) {
  const [isExpanding, setExpanding] = useState(false);
  const [isExpanded, setExpanded] = useState(false);
  const [isCollapsing, setCollapsing] = useState(false);
  const [showIframe, setShowIframe] = useState(false);
  const wasExpanded = useRef(false);
  const collapseTimeoutId = useRef(0);
  const expandingTimeoutId = useRef(0);

  /**
   * scroll locking will either be enabled or disabled for the whole session,
   * hence it is 'okay' to have these hooks inside an if-statement.
   */
  if (USE_SCROLL_LOCKING_FEATURE) {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useForceHeaderCollapsed(isExpanding);
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useScrollLocking(isExpanding);
  }
  const openIframe = () => {
    if (disabled) {
      return;
    }
    setExpanding(true);
    wasExpanded.current = true;

    // force collapse of the left aside when opening a harmonica iframe
    pm2InterfaceStore.dispatch(pm2InterfaceStore.actions.closeLeftAside());

    // We only wish to show the iframe once the user has interacted with the
    // component, in order to preserve unnecessary processing
    if (!showIframe) {
      setShowIframe(true);
    }
    const componentRoot = rootRef.current;

    // In case we couldn't access the componentRoot, then we cannot enable auto
    // -scrolling
    if (!componentRoot) {
      return;
    }
    const bbox = componentRoot.getBoundingClientRect();
    const desiredTop = pm2Header.getCurrentHeight({
      forceCollapsed: true
    });

    // Scroll us to the yOffset where the accordion will be positioned exactly
    // under the collapsed header.
    const newYOffset = bbox.top + window.pageYOffset - desiredTop;
    scrollTo(newYOffset, {
      minDurationMs: HEIGHT_TRANSITION_DURATION_MS,
      maxDurationMs: HEIGHT_TRANSITION_DURATION_MS,
      disableMaxScroll: true,
      forceAutoScroll: true
    });
    window.clearTimeout(expandingTimeoutId.current);
    expandingTimeoutId.current = window.setTimeout(() => {
      setExpanded(true);
    }, HEIGHT_TRANSITION_DURATION_MS);
  };
  const closeIframe = () => {
    if (disabled) {
      return;
    }
    setExpanding(false);
    setExpanded(false);
    setCollapsing(true);
    window.clearTimeout(collapseTimeoutId.current);
    collapseTimeoutId.current = window.setTimeout(() => {
      setCollapsing(false);
    }, HEIGHT_TRANSITION_DURATION_MS);

    // Stop autoscrolling in case the user closes the component before the auto
    // scroll has been fully finished.
    stopScrolling();
  };
  const toggleIframe = () => {
    if (isExpanded) {
      closeIframe();
    } else {
      openIframe();
    }
  };
  return [isExpanding, isExpanded, isCollapsing, wasExpanded.current, showIframe, toggleIframe];
}

export { useExpandableIframe };
//# sourceMappingURL=hook.expandable.js.map
